import { InstantAnswerContext } from "@faire/help-center-self-serve/types";
import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalOrders from "@faire/web-api--source/routes/www/brand-portal/orders";
import brandPortalOrdersbrandOrderTokenEdit from "@faire/web-api--source/routes/www/brand-portal/orders/brandOrderToken/edit";
import React, { type JSX } from "react";

import { Bold } from "../common/Bold";

interface AddOrEditItemsIAProps {
  context: InstantAnswerContext;
}

export const AddOrEditItemsIA: React.FC<AddOrEditItemsIAProps> = ({
  context,
}) => {
  const brandOrderToken = context.brandOrder?.brand_order?.token;
  if (brandOrderToken) {
    return (
      <Column>
        <Typography>
          <StrictLocalMsg
            defaultMessage="To add or edit products in an order, first go to the <OrderDetailsLinkFormatting>order details page</OrderDetailsLinkFormatting>. Then select <LinkFormatting>Edit Order</LinkFormatting>."
            values={{
              LinkFormatting: (chunks: JSX.Element) =>
                EditOrderLinkFormatting(chunks, brandOrderToken),
              OrderDetailsLinkFormatting: (chunks: JSX.Element) =>
                orderDetailsLinkFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/452e1f0292ecf47f0f39e2864ea09e44d74fe273b8da8827cb69fb5368324a2d.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="You can add products by clicking the <BoldFormatting>Add Product</BoldFormatting> button, and remove them by clicking the trash can icon next to each item. You can update item quantities by changing the number in the <BoldFormatting>Qty</BoldFormatting> column."
            values={{
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/452e1f0292ecf47f0f39e2864ea09e44d74fe273b8da8827cb69fb5368324a2d.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="After making your edits, click <BoldFormatting>Update</BoldFormatting> to save them. You’ll also have the option to leave a note for the retailer, which we highly recommend so they know what’s happening with their order. The retailer will receive an email notification detailing the changes."
            values={{
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/452e1f0292ecf47f0f39e2864ea09e44d74fe273b8da8827cb69fb5368324a2d.png",
            }}
          />
        </Typography>
      </Column>
    );
  } else {
    return (
      <Column>
        <Typography>
          <StrictLocalMsg
            defaultMessage="To add or edit products in an order, first go to your <BoldFormatting>Orders</BoldFormatting>, and navigate to the <BoldFormatting>order detail</BoldFormatting> page for the order you’d like to change. Then select <BoldFormatting>Edit Order</BoldFormatting>."
            values={{
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/a54248b9c042de45bf79639c71924721ab8414261615827c8379771d0f5d9eaf.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="You can add products by clicking the <BoldFormatting>Add Product</BoldFormatting> button, and remove them by clicking the trash can icon next to each item. You can update item quantities by changing the number in the <BoldFormatting>Qty</BoldFormatting> column."
            values={{
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/a54248b9c042de45bf79639c71924721ab8414261615827c8379771d0f5d9eaf.png",
            }}
          />
        </Typography>
        <Spacer height="1x" />
        <Typography>
          <StrictLocalMsg
            defaultMessage="After making your edits, click <BoldFormatting>Update</BoldFormatting> to save them. You’ll also have the option to leave a note for the retailer, which we highly recommend so they know what’s happening with their order. The retailer will receive an email notification detailing the changes."
            values={{
              BoldFormatting: (chunks: JSX.Element) => BoldFormatting(chunks),
            }}
            description={{
              img: "https://cdn.faire.com/fastly/a54248b9c042de45bf79639c71924721ab8414261615827c8379771d0f5d9eaf.png",
            }}
          />
        </Typography>
      </Column>
    );
  }
};

const EditOrderLinkFormatting = (
  chunks: JSX.Element,
  brandOrderToken: string
) => (
  <Button
    variant="tertiary"
    linkTo={brandPortalOrdersbrandOrderTokenEdit(brandOrderToken)}
    target="_blank"
  >
    <Bold>{chunks}</Bold>
  </Button>
);

const orderDetailsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalOrders()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);
const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
